/* Hallmark · build · DcViewerPage · genre: modern-minimal · macrostructure: Workbench (viewer)
 * theme: Cobalt (project-locked) · pre-emit critique: P5 H5 E5 S5 R5 V5
 * nav: N3 side-rail (embedded in layout) · footer: none (app shell)
 * states: load · empty · populated · inspecting · searching
 * touch: all controls >= 44px · keyboard: Ctrl+K search focus, Escape dismiss drawer
 */

import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Menu, X, Edit3 } from 'lucide-react';
import { api } from '../utils/api';
import { DcMapSVG } from '../components/calculator/DcMapSVG';
import { RackFlyoutDrawer, type RackDetail } from '../components/viewer/RackFlyoutDrawer';
import { useDcStore } from '../stores/dcStore';
import type { RackMeta } from '../hooks/useCalculatorLogic';
import { formatRackName } from '../utils/rackName.logic';
import { editorHref } from '../utils/floorPlan.logic';
import { getRackTemperature } from '../utils/thermalOverlay.logic';

// ─── Stat chip ─────────────────────────────────────────────────────────────────
function StatChip({ label, value, accent }: { label: string; value: string | number; accent?: string }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: 1,
      padding: '8px 12px',
      background: 'rgba(255,255,255,0.03)',
      border: '1px solid rgba(255,255,255,0.07)',
      borderRadius: 7, minWidth: 70, flex: 1,
    }}>
      <span style={{ fontSize: 10, color: 'var(--text-muted, #6b7280)' }}>{label}</span>
      <span style={{ fontSize: 15, fontWeight: 700, color: accent || 'var(--text-primary, #e2e8f0)', fontFamily: 'JetBrains Mono, monospace' }}>
        {value}
      </span>
    </div>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────────
export default function DcViewerPage() {
  const { code: dcCode } = useParams<{ code: string }>();
  const [searchParams] = useSearchParams();
  const { datacenters, fetchDatacenters, loading: dcsLoading } = useDcStore();
  const dc = datacenters.find(d => d.code === dcCode);

  const [selectedRack, setSelectedRack] = useState<RackDetail | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (datacenters.length === 0 && !dcsLoading) {
      fetchDatacenters();
    }
  }, [datacenters.length, fetchDatacenters, dcsLoading]);

  // Fetch rack list
  const { data: racksData, isLoading } = useQuery<RackMeta[]>({
    queryKey: ['dc-viewer-racks', dcCode],
    queryFn: () => api.get(`/api/datacenters/${dcCode}/racks`).then((r: any) => r.data),
    enabled: !!dcCode,
  });

  // Fetch telemetry for thermal overlay
  const { data: telemetryData } = useQuery<Record<string, any>>({
    queryKey: ['dc-viewer-telemetry', dcCode],
    queryFn: () => api.get(`/api/datacenters/${dcCode}/telemetry-mock`).then((r: any) => r.data).catch(() => null),
    enabled: !!dcCode,
    refetchInterval: 30000,
  });

  const racks: RackMeta[] = racksData ?? [];

  // Filter by search
  const filteredRacks = useMemo(() => {
    if (!searchQuery.trim()) return racks;
    const q = searchQuery.toLowerCase();
    return racks.filter(r =>
      (r.label || '').toLowerCase().includes(q) ||
      String(r.row_index + 1).includes(q) ||
      (r.tenant || '').toLowerCase().includes(q)
    );
  }, [racks, searchQuery]);

  // Summary stats
  const stats = useMemo(() => {
    const total = racks.length;
    const blocked = racks.filter(r => r.is_blocked).length;
    return { total, blocked, active: total - blocked };
  }, [racks]);

  const handleRackClick = useCallback((r: RackMeta) => {
    setSelectedRack({
      id: r.id!,
      label: r.label,
      row_index: r.row_index,
      rack_index: r.rack_index,
      u_slots: r.heightU || 42,
      type: 'server_rack',
      is_blocked: r.is_blocked || false,
      tenant: r.tenant || 'mine',
      status: (r as any).status || 'active',
      role: (r as any).role,
      max_power_w: (r as any).max_power_w,
      max_weight_kg: (r as any).max_weight_kg,
    });
  }, []);

  // Keyboard shortcut Ctrl+K / Cmd+K to focus search
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSidebarOpen(true);
        setTimeout(() => {
          inputRef.current?.focus();
          inputRef.current?.select();
        }, 50);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // URL query param ?rack=ID direct focus on load
  useEffect(() => {
    const rackId = searchParams.get('rack');
    if (rackId && racks.length > 0) {
      const match = racks.find(r => String(r.id) === String(rackId));
      if (match) {
        handleRackClick(match);
      }
    }
  }, [searchParams, racks, handleRackClick]);

  useEffect(() => {
    document.title = dc ? `${dc.name} — План зала | DC Cable Link` : 'План зала | DC Cable Link';
  }, [dc]);

  if (isLoading || dcsLoading || !dc) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--text-muted, #6b7280)', flexDirection: 'column', gap: 12 }}>
        <div style={{ width: 32, height: 32, border: '2px solid rgba(59,130,246,0.3)', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <span style={{ fontSize: 13 }}>Загрузка плана зала...</span>
        <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex', height: '100%',
      background: 'var(--bg-app, #0b0e14)',
      overflow: 'hidden',
      fontFamily: 'Inter, system-ui, sans-serif',
      position: 'relative',
    }}>
      {/* ── Left panel: info + controls ── */}
      {sidebarOpen && (
        <aside style={{
          width: 280,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--bg-panel, #0d1117)',
          borderRight: '1px solid var(--border, #1a2744)',
          overflow: 'hidden',
          zIndex: 20,
        }}>
          {/* DC header */}
          <div style={{
            padding: '14px 16px 12px',
            borderBottom: '1px solid var(--border, #1a2744)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted, #6b7280)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>
                План зала ЦОД
              </div>
              <h1 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary, #e2e8f0)', margin: 0, lineHeight: 1.3 }}>
                {dc.name}
              </h1>
              <div style={{ fontSize: 11, color: 'var(--text-muted, #6b7280)', marginTop: 3 }}>
                {dc.code}
              </div>
            </div>

            {/* Mobile close sidebar button (>= 44px touch) */}
            <button
              onClick={() => setSidebarOpen(false)}
              className="md:hidden"
              aria-label="Скрыть боковую панель"
              style={{
                width: 44, height: 44,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'transparent', border: 'none',
                color: 'var(--text-muted, #6b7280)', cursor: 'pointer',
              }}
            >
              <X size={18} />
            </button>
          </div>

          {/* Stats */}
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border, #1a2744)', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <StatChip label="Стойки" value={stats.total} />
            <StatChip label="Активных" value={stats.active} accent="#22c55e" />
            <StatChip label="Рядов" value={dc.rows_count} />
          </div>

          {/* Rack search (Ctrl+K) */}
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border, #1a2744)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted, #6b7280)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                Поиск стойки
              </span>
              <kbd style={{
                fontSize: 9, fontFamily: 'monospace',
                padding: '2px 5px', borderRadius: 4,
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.12)',
                color: 'var(--text-muted, #6b7280)',
              }}>
                Ctrl+K
              </kbd>
            </div>

            <div style={{ position: 'relative' }}>
              <input
                ref={inputRef}
                data-testid="viewer-rack-search"
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && filteredRacks.length > 0) {
                    handleRackClick(filteredRacks[0]);
                  }
                }}
                placeholder="Метка, ряд или арендатор..."
                style={{
                  width: '100%', boxSizing: 'border-box',
                  minHeight: 44,
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 6, padding: '10px 12px',
                  fontSize: 12, color: 'var(--text-primary, #e2e8f0)',
                  outline: 'none',
                }}
              />
            </div>

            {searchQuery && (
              <div style={{ marginTop: 8 }}>
                <div style={{ fontSize: 11, color: 'var(--text-muted, #6b7280)', marginBottom: 6 }}>
                  Найдено: {filteredRacks.length} из {racks.length}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 180, overflowY: 'auto' }}>
                  {filteredRacks.slice(0, 10).map(r => (
                    <button
                      key={r.id}
                      data-testid={`search-result-rack-${r.id}`}
                      onClick={() => handleRackClick(r)}
                      style={{
                        minHeight: 44,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '8px 10px',
                        background: selectedRack?.id === r.id ? 'rgba(56,189,248,0.15)' : 'rgba(255,255,255,0.03)',
                        border: `1px solid ${selectedRack?.id === r.id ? 'rgba(56,189,248,0.4)' : 'rgba(255,255,255,0.06)'}`,
                        borderRadius: 6,
                        cursor: 'pointer',
                        textAlign: 'left',
                        color: 'var(--text-primary, #e2e8f0)',
                      }}
                    >
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 600 }}>
                          {r.label || formatRackName(undefined, undefined, r.row_index, r.rack_index)}
                        </div>
                        <div style={{ fontSize: 10, color: 'var(--text-muted, #6b7280)' }}>
                          Ряд {r.row_index + 1} · Поз. {r.rack_index + 1}
                        </div>
                      </div>
                      <span style={{ fontSize: 10, color: '#38bdf8', fontWeight: 500 }}>
                        Выбрать →
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Legend */}
          <div style={{ padding: '12px 16px', flex: 1, overflowY: 'auto' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted, #6b7280)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>
              Легенда
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {[
                { color: '#60a5fa', label: 'Лоток (трасса)' },
                { color: '#ef4444', label: 'Заблокировано / Вывод' },
                { color: '#38bdf8', label: 'Выбранный шкаф (фокус)' },
              ].map(item => (
                <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 10, height: 10, borderRadius: 2, background: item.color, flexShrink: 0 }} />
                  <span style={{ fontSize: 11, color: 'var(--text-muted, #6b7280)' }}>{item.label}</span>
                </div>
              ))}
            </div>

            {/* Temperature Legend (ASHRAE) */}
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted, #6b7280)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>
                Температура (ASHRAE)
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {[
                  { color: '#0284c7', label: '< 25°C · Норма' },
                  { color: '#f59e0b', label: '25–30°C · Нагрев' },
                  { color: '#ef4444', label: '> 30°C · Перегрев' },
                ].map(item => (
                  <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 10, height: 10, borderRadius: 2, background: item.color, flexShrink: 0 }} />
                    <span style={{ fontSize: 11, color: 'var(--text-muted, #6b7280)' }}>{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Link to editor (>= 44px touch) */}
          <div style={{ padding: '10px 16px', borderTop: '1px solid var(--border, #1a2744)' }}>
            <Link
              to={editorHref(dcCode!)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                minHeight: 44,
                padding: '10px 12px', borderRadius: 6,
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(255,255,255,0.08)',
                color: 'var(--text-muted, #6b7280)', fontSize: 12,
                textDecoration: 'none',
              }}
            >
              <Edit3 size={14} />
              <span>Открыть редактор</span>
            </Link>
          </div>
        </aside>
      )}

      {/* ── Main canvas area ── */}
      <main style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        <DcMapSVG
          racks={filteredRacks}
          route={[]}
          onRackClick={(r: any) => handleRackClick(r as RackMeta)}
          dc={dc}
          focusedRackId={selectedRack?.id}
          onEmptyClick={() => setSelectedRack(null)}
          telemetryMap={telemetryData}
        />

        {/* Top-left bar: Sidebar toggle (on mobile) + Read-only badge */}
        <div style={{
          position: 'absolute', top: 12, left: 12,
          display: 'flex', alignItems: 'center', gap: 8,
          zIndex: 10,
        }}>
          {!sidebarOpen && (
            <button
              onClick={() => setSidebarOpen(true)}
              aria-label="Показать панель управления"
              style={{
                minWidth: 44, minHeight: 44,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'rgba(13,17,23,0.9)',
                border: '1px solid var(--border, #1a2744)',
                borderRadius: 8,
                color: 'var(--text-primary, #e2e8f0)',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
              }}
            >
              <Menu size={18} />
            </button>
          )}

          <div style={{
            minHeight: 32,
            display: 'flex', alignItems: 'center',
            padding: '4px 12px', borderRadius: 20,
            background: 'rgba(0,0,0,0.7)',
            border: '1px solid rgba(255,255,255,0.1)',
            fontSize: 11, color: 'var(--text-muted, #6b7280)',
            backdropFilter: 'blur(4px)',
            pointerEvents: 'none',
          }}>
            👁 Просмотр — без изменений
          </div>
        </div>

        {/* Slide-out Rack Inspector Drawer */}
        <RackFlyoutDrawer
          rack={selectedRack}
          dcCode={dcCode!}
          onClose={() => setSelectedRack(null)}
          temperature={selectedRack ? getRackTemperature(selectedRack, telemetryData) : null}
        />
      </main>
    </div>
  );
}
